import React from "react";
import CreateAccount from "./components/CreateAppointment";
import NavigoDashboard from "./components/NavigoDashboard";
import DocumentUpload from "./components/DocumentUpload";

/* A patient id read off the query string. Number(null) is 0 and Number("abc")
   is NaN, so an absent or junk param fails this test and the caller is treated
   as having passed nothing at all — which is what keeps the two entry points
   that exist today byte-identical: the drawer's bare /navigo-module
   (constants/Navigation.js:49 -> Drawer.js:134 -> Helper.navigateTo) and
   RefundDashboard.js:970's ?view=2&appId=<id>. */
const toPatientId = (raw) => {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
};

class NavigoModule extends React.Component {
  constructor(props) {
    super(props);
    const searchParams = new URLSearchParams(props.location.search);

    const qpView = Number(searchParams.get("view"));
    const qpAppId = searchParams.get("appId");
    const qpPatientId = toPatientId(searchParams.get("patientId"));

    const view = qpView || 0;

    /* Where "back" goes when a patient-scoped appointments-v2 board opened this
       module. An instance field, not state: the query string is read here and
       nowhere else, so it is fixed for the life of the mount, and it must not
       take part in rendering. Null for every caller that exists today.

       It carries an ID, not a path, deliberately: the legacy side rebuilds
       /appointments-v2/<id> itself, so there is nowhere for a caller to put an
       arbitrary route and the history.replace below can only ever land on that
       one board. `returnPid` is accepted as an alias for the same value because
       the two drafts of this contract named the param differently; `patientId`
       is the canonical spelling and the one the v2 board should emit. */
    const returnPid = qpPatientId || toPatientId(searchParams.get("returnPid"));
    this.returnTo = returnPid ? `/appointments-v2/${returnPid}` : null;

    /* `props` is handed to all three children (render(), below), so its shape
       is load-bearing: today it comes out as exactly null (no params) or
       exactly {appId: N}, and DocumentUpload has to keep receiving that same
       object for RefundDashboard.js:970's deep link. The patient seed is
       therefore added only for the create view — CreateAppointment is the one
       child that reads it, and NavigoDashboard reads `data` not at all. */
    const seed = {};
    if (qpAppId) seed.appId = Number(qpAppId);
    if (view === 1 && qpPatientId) seed.patientId = qpPatientId;

    this.state = {
      view,
      props: Object.keys(seed).length ? seed : null,

      dashboardKey: 0,
      dashboardKeyReload: 0,
    };
  }

  setView = (v, p) => {
    /* Opened from a patient-scoped v2 board: leaving a child screen means
       leaving this module, not revealing the ops-wide dashboard parked behind
       it at display:none. This single interception is the whole return path,
       because setView(0) is the only way either child gets back —
       DocumentUpload has exactly one `setView` at :1721, inside exitPage
       (1697-1722), whose one caller is the back arrow at :2798;
       CreateAppointment's is at :1451, inside exitPage (1449-1452), called by
       the back arrow at :2564 and the Cancel button at :2584. That is also why
       DocumentUpload itself stays untouched: cart-module/index.js:81 and
       slot-margin/index.js:59 mount it too, and a returnTo prop would have to
       be supplied by all three hosts.

       Guarding on `v === 0` leaves every INBOUND transition provably unchanged:
       changeScreen's setView(1, null) / setView(2, {appId}) at
       NavigoDashboard.js:243-252, and the payment-link flow's setView(2,
       {appId}) (payFromBridge/states/SuccessWithGeneratedLink.js:192,
       AptCreatedButLinkNotGenerated.js:47,
       SuccessTotalPaymentWithWallet.js:33). That last one is how a booking that
       ends in a payment link still reaches the board rather than
       short-circuiting to it: "Done" opens the legacy details screen, and the
       details back arrow then comes through here.

       HOW it returns decides what the browser's own Back does afterwards. The
       board PUSHED us, so the stack is [board, /navigo-module].
         push(board)    -> [board, navigo, board]: Back re-opens the screen the
                           operator just left.
         replace(board) -> [board, board]: Back appears to do nothing, and it
                           takes a second Back to leave the board.
         goBack()       -> [board]: the operator is on the very entry they left,
                           and Back behaves as if they had never gone.
       So goBack() — but ONLY when the board is provably the entry behind us,
       which is what the board's `fromAppointmentsV2` history state records
       (appointments-v2/index.js, both pushes). Without it — a pasted URL, a new
       tab, a bookmark — the entry behind could be anything, including another
       site, so replace() lands on the board without leaving the app. History
       state survives a reload of the same entry, so a refreshed details screen
       still goes back correctly. */
    if (v === 0 && this.returnTo) {
      const st = this.props.location && this.props.location.state;
      if (st && st.fromAppointmentsV2) this.props.history.goBack();
      else this.props.history.replace(this.returnTo);
      return;
    }
    this.setState({ view: v, props: p });
  };
  resetDashboard = () => {
    /* Defensive only — there is no live caller. The one call site,
       CreateAppointment.js:1157, sits inside a local createAppointment() (1150)
       whose only invocation is commented out at CreateAppointment.js:1002 — the
       live create goes through startPaymentLinkFlow on the next line. Were it
       ever re-enabled it runs `props.resetDashboard(); exitPage();` back to
       back (1157-1158), and on a v2 round trip the key bump would remount the
       hidden ops-wide dashboard and fire a full POST /v1/appointment/filter
       into a tree that the exitPage on the very next line tears down.

       resetDashboardReload below must NOT get the same guard: DocumentUpload
       calls it at :981 and :1007-1008 while the operator is still ON the
       details screen, refreshing the list behind it. */
    if (this.returnTo) return;
    this.setState((prev) => ({ dashboardKey: prev.dashboardKey + 1 }));
  };
  resetDashboardReload = () => {
    this.setState((prev) => ({
      dashboardKeyReload: prev.dashboardKeyReload + 1,
    }));
  };

  render() {
    return (
      <>
        <div style={{ display: this.state.view === 0 ? "block" : "none" }}>
          <NavigoDashboard
            key={this.state.dashboardKey}
            setView={this.setView}
            data={this.state.props}
            history={this.props.history}
            dashboardKeyReload={this.state.dashboardKeyReload}
          />
        </div>
        {this.state.view === 1 ? (
          <CreateAccount
            setView={this.setView}
            data={this.state.props}
            resetDashboard={this.resetDashboard}
          />
        ) : this.state.view === 2 ? (
          <DocumentUpload
            setView={this.setView}
            data={this.state.props}
            resetDashboardReload={this.resetDashboardReload}
          />
        ) : (
          <></>
        )}
      </>
    );
  }
}

export default NavigoModule;
